/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonIcon from '@mui/icons-material/Person';

import Chip from '@shared/atoms/Chip';
import Icon from '@shared/atoms/Icon';
import IconButton from '@shared/atoms/IconButton';
import FormAutocomplete from '@shared/atoms/FormFields/FormAutocomplete';
import FormSelect from '@shared/atoms/FormFields/FormSelect';
import FullWidthTabs from '@shared/atoms/FullWidthTabs';
import T from '@shared/atoms/Typography';
import { membersRoles } from '@shared/const';
import Dialog from '@shared/molecules/Dialog';
import Menu from '@shared/molecules/Menu';
import theme from '@shared/themes/default';

import CircularSpinner from '@shared/atoms/CircularSpinner';
import { useAppGlobals } from '../../../contexts/AppContext';
import MembershipsTab from './tabs/MembershipsTab';
import SubscriptionsTab from './tabs/SubscriptionsTab';

import { deleteUser, updateMember } from '../graphql/mutations';
import { getUser, listOrganizations } from '../graphql/queries';

const OuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${theme.spacing.m1} ${theme.spacing.m1} 0 ${theme.spacing.m1};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.s};
`;

const LeftTitleWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.s};
`;

const RightTitleWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.m1};
`;

const ChipWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.s};
`;

const UsersTableRowItem = ({
  item,
  onDeleteUser,
  setErrorMessage,
  setHasError,
  setHasSuccess,
  setSuccessMessage,
}) => {
  const { useLazyGraphQLQuery, useGraphQLMutation } = useAppGlobals();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moveOrganizationDialogOpen, setMoveOrganizationDialogOpen] =
    useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedOrganization, setSelectedOrganization] = useState();
  const [selectedRole, setSelectedRole] = useState();

  const customMenuHandler = (event) => {
    setAnchorEl(anchorEl === null ? event.currentTarget : null);
  };

  const [getUserQuery, getUserResponse] = useLazyGraphQLQuery(getUser);
  const [listOrganizationsQuery, listOrganizationsResponse] =
    useLazyGraphQLQuery(listOrganizations);

  const [updateMemberMutation, updateMemberResponse] = useGraphQLMutation(
    updateMember,
    {
      update(cache) {
        cache.modify({
          fields: {
            listMembers(_, { DELETE }) {
              return DELETE;
            },
          },
        });
      },
      onCompleted() {
        setSuccessMessage(
          t('backoffice.users.successfullyMoved', {
            name: item.name,
            email: item.email,
            organization: selectedOrganization.name,
          })
        );
        setHasSuccess(true);
        setMoveOrganizationDialogOpen(false);
      },
      onError() {
        setErrorMessage(
          t('backoffice.users.failedToMove', {
            name: item.name,
            email: item.email,
            organization: selectedOrganization.name,
          })
        );
        setHasError(true);
        setMoveOrganizationDialogOpen(false);
      },
    }
  );
  const [deleteUserMutation, deleteUserResponse] = useGraphQLMutation(
    deleteUser,
    {
      update(cache, { data: { deleteUser } }) {
        cache.evict({ id: cache.identify(deleteUser) });
        cache.gc();
      },
      onCompleted({ deleteUser }) {
        onDeleteUser(deleteUser.id);
        setSuccessMessage(
          t('backoffice.users.successfullyDeleted', {
            name: item.name,
            email: item.email,
          })
        );
        setHasSuccess(true);
        setDeleteDialogOpen(false);
      },
      onError() {
        setErrorMessage(
          t('backoffice.users.failedToDelete', {
            name: item.name,
            email: item.email,
          })
        );
        setHasError(true);
        setDeleteDialogOpen(false);
      },
    }
  );

  useEffect(() => {
    if (item.__typename !== 'Invitation') {
      getUserQuery({
        variables: {
          id: item.id,
        },
      });
    }
  }, [item]);

  useEffect(() => {
    if (moveOrganizationDialogOpen) {
      listOrganizationsQuery({
        variables: {
          pagination: { limit: 1000 },
        },
      });
    }
  }, [moveOrganizationDialogOpen]);

  const processedOrganizations = (
    listOrganizationsResponse.data?.listOrganizations || []
  )
    .filter((item) => !item.hasActiveScimClient)
    .map((item) => ({
      name: item.name || `No name (ID: ${item.id})`,
      value: item.id,
    }))
    .sort((a, b) => {
      return a.name?.localeCompare(b.name);
    });

  const userData = getUserResponse.data?.getUser;

  const subscriptions = (userData?.memberships || [])
    .map((membership) => membership.organization.subscription)
    .filter((subscription) => subscription);

  if (userData?.subscriptions) {
    subscriptions.push(...userData.subscriptions);
  }

  const isInvitation = item.__typename === 'Invitation';

  const hasScimMembership =
    userData?.memberships?.some(
      (membership) => membership.organization?.hasActiveScimClient
    ) ||
    userData?.invitations?.some(
      (invitation) => invitation.organization?.hasActiveScimClient
    );

  return (
    <>
      <OuterWrapper>
        <TitleWrapper>
          <LeftTitleWrapper>
            <Icon icon={PersonIcon} />
            <T fontWeight="semibold" variant="subtitle">
              {item.name}
            </T>
          </LeftTitleWrapper>
          {!isInvitation && (
            <RightTitleWrapper>
              <ChipWrapper>
                {getUserResponse.loading && (
                  <CircularSpinner static size={25} />
                )}
                {!getUserResponse.loading && userData?.cognito && (
                  <>
                    <Chip
                      label={
                        userData.cognito.enabled
                          ? t('backoffice.users.active')
                          : t('backoffice.users.inactive')
                      }
                      small
                      theme="dark"
                    />
                    <Chip
                      color={
                        userData.cognito.status === 'CONFIRMED'
                          ? undefined
                          : theme.palette.errorLight
                      }
                      label={userData.cognito.status}
                      small
                      theme="dark"
                    />
                  </>
                )}
              </ChipWrapper>
              {!hasScimMembership && (
                <>
                  <IconButton
                    ariaLabel="More"
                    icon={MoreHorizIcon}
                    onClick={customMenuHandler}
                    theme="dark"
                    type="primary"
                  />
                  <Menu
                    freeMenu
                    initAnchorEl={anchorEl}
                    menuItems={[
                      {
                        label: t('backoffice.users.deleteUser'),
                        handler: () => setDeleteDialogOpen(true),
                      },
                      {
                        label: t('backoffice.users.moveToOrganization'),
                        handler: () => {
                          setSelectedRole(membersRoles.member);
                          setSelectedOrganization();
                          setMoveOrganizationDialogOpen(true);
                        },
                      },
                    ]}
                    setInitAnchorEl={setAnchorEl}
                    theme="dark"
                  />
                </>
              )}
            </RightTitleWrapper>
          )}
        </TitleWrapper>
        {isInvitation ? (
          <T>
            {t('backoffice.users.userInvitedBy', {
              organization: item.organization.name,
              user: item.invitedBy?.name || item.source,
            })}
          </T>
        ) : (
          <FullWidthTabs
            onChangeTabHandler={(index) => setSelectedTab(index)}
            selectedTabIndex={selectedTab}
            tabsToSelect={[
              {
                label: t('backoffice.users.memberships'),
                component: (
                  <MembershipsTab
                    loading={getUserResponse.loading}
                    memberships={userData?.memberships}
                  />
                ),
              },
              {
                label: t('backoffice.users.subscriptions'),
                component: (
                  <SubscriptionsTab
                    loading={getUserResponse.loading}
                    subscriptions={subscriptions}
                  />
                ),
              },
            ]}
            theme="dark"
          />
        )}
      </OuterWrapper>
      <Dialog
        ariaLabel={t('backoffice.users.deleteUser')}
        actions={[
          {
            label: t('backoffice.users.cancel'),
            handler: () => setDeleteDialogOpen(false),
            type: 'secondaryOutline',
          },
          {
            label: t('backoffice.users.confirm'),
            handler: () => deleteUserMutation({ variables: { id: item.id } }),
          },
        ]}
        loading={deleteUserResponse.loading}
        onClose={() => setDeleteDialogOpen(false)}
        open={deleteDialogOpen}
        title={t('backoffice.users.deleteUser')}
      >
        <T color={theme.palette.mainDark}>
          {t('backoffice.users.confirmDelete', {
            name: item.name,
            email: item.email,
          })}
        </T>
        <br />
        <T color={theme.palette.pureDark} fontWeight="bold">
          {t('backoffice.users.cannotBeUndone')}
        </T>
      </Dialog>
      <Dialog
        ariaLabel={t('backoffice.users.moveToOrganization')}
        actions={[
          {
            label: t('backoffice.users.cancel'),
            handler: () => setMoveOrganizationDialogOpen(false),
            type: 'secondaryOutline',
          },
          {
            label: t('backoffice.users.confirm'),
            disabled: !selectedOrganization || !selectedRole,
            handler: () =>
              updateMemberMutation({
                variables: {
                  input: {
                    organizationId: selectedOrganization.value,
                    role: selectedRole,
                    userId: item.id,
                  },
                },
              }),
          },
        ]}
        loading={updateMemberResponse.loading}
        onClose={() => setMoveOrganizationDialogOpen(false)}
        open={moveOrganizationDialogOpen}
        title={t('backoffice.users.moveToOrganization')}
      >
        <FormAutocomplete
          id="organization"
          name="organization"
          filterOptions
          getOptionKey={(option) => option.value}
          getOptionLabel={(option) => option.name}
          handleChange={({ value }) => setSelectedOrganization(value)}
          label={t('backoffice.users.organization')}
          loading={listOrganizationsResponse.loading}
          options={processedOrganizations}
          placeholder={t('backoffice.users.selectOrganization')}
          required
          sx={{ marginBottom: theme.spacing.m1 }}
          theme="light"
        />
        <FormSelect
          id="role"
          name="role"
          items={[
            {
              value: membersRoles.member,
              key: membersRoles.member,
              label: t('administrateUsers.member'),
            },
            {
              value: membersRoles.manager,
              key: membersRoles.manager,
              label: t('administrateUsers.manager'),
            },
            {
              value: membersRoles.admin,
              key: membersRoles.admin,
              label: t('administrateUsers.administrator'),
            },
          ]}
          handleChange={({ value }) => setSelectedRole(value)}
          label={t('backoffice.users.role')}
          required
          theme="light"
          value={selectedRole}
        />
      </Dialog>
    </>
  );
};

UsersTableRowItem.propTypes = {
  item: PropTypes.object.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setHasError: PropTypes.func.isRequired,
  setHasSuccess: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
};

export default UsersTableRowItem;
